var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Autocomplete, autocompleteClasses, Box, Checkbox, FormControl, OutlinedInput, Popper, styled, } from '@mui/material';
import React from 'react';
import { FixedSizeList } from 'react-window';
import { useCommonWebTranslation } from '../../hooks';
import { themeColors } from '../../JuvareTheme';
import { TypographyWithOverflow } from './TypographyWithOverflow';
const listboxPadding = 8;
const formatTags = () => null;
// this is interesting trick to pass some of the control to AutoComplete by forwarding array of parameters as react node,
// that will be attached to custom element.
// taken from MUI example
const renderOption = (props, option, state) => [props, option, state];
const RenderRow = (rowProps) => {
    var _a, _b;
    const { data, index, style } = rowProps;
    const item = data[index];
    // unfolding parameters received from Autocomplete
    // we expect to get from there stuff like event handlers, which will give us interaction handling for free
    const [autoCompleteItemProps, option, state] = item;
    // as long as react-window uses absolute positioning, we need manually to add padding
    const paddedStyle = Object.assign(Object.assign({}, style), { top: `${parseFloat((_b = (_a = style.top) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '0') + listboxPadding}px` });
    return (React.createElement(Box, Object.assign({}, autoCompleteItemProps, { style: paddedStyle, role: 'option', tabIndex: '-1', component: 'li' }),
        React.createElement(Checkbox, { color: 'primary', checked: state.selected, "aria-label": option.value }),
        React.createElement(TypographyWithOverflow, { component: 'span' }, option.label)));
};
// need this to remove all the extra padding from inner list, rendered by virtualization
const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
    },
});
// another interesting trick to pass some of the control from AutoComplete component
const OuterElementContext = React.createContext({});
const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return React.createElement("div", Object.assign({ ref: ref }, props, outerProps, { "data-test": 'virtual-list' }));
});
// Adapter replacing standard Autocomplete's ListBox with version using react-window
const ListboxComponent = React.forwardRef((props, ref) => {
    const { children } = props, other = __rest(props, ["children"]);
    const itemData = [];
    children.forEach((item) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
    });
    const itemCount = itemData.length;
    // measured manually by comparing list item height in DataTable's filter multi select component
    const itemSize = 54;
    // max height is 8 items size
    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemCount * itemSize;
    };
    return (React.createElement("div", { ref: ref },
        React.createElement(OuterElementContext.Provider, { value: other },
            React.createElement(FixedSizeList, { itemData: itemData, height: getHeight() + 2 * listboxPadding, width: '100%', outerElementType: OuterElementType, innerElementType: 'ul', itemSize: itemSize, overscanCount: 5, itemCount: itemCount, style: { margin: 0 } }, RenderRow))));
});
/**
 * A multi-select input component with virtualization support via `react-window`,
 * optimized for performance with large lists of selectable items.
 *
 * @param {VirtualizedMultiSelectProps} props - The props for the VirtualizedMultiSelect component.
 * @param {FilterConfigSelectMany} props.config - Filter configuration containing selectable items.
 * @param {string[]} props.value - Currently selected values.
 * @param {boolean} [props.error] - Optional error state flag.
 * @param {(values: string[]) => void} props.onSelectionChange - Callback triggered when selection changes.
 *
 * @returns {JSX.Element} The rendered Autocomplete multi-select with virtualization.
 */
export const VirtualizedMultiSelect = (props) => {
    var _a, _b, _c;
    const { t } = useCommonWebTranslation();
    const labelWithChipCount = t('dataFilter.selectedCount', {
        count: (_b = (_a = props.value) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0,
        label: (_c = props.config.label) !== null && _c !== void 0 ? _c : t('dataFilter.noLabelFallbackForSelection'),
    });
    const renderInput = (params) => {
        return (React.createElement(FormControl, { fullWidth: params.fullWidth, size: params.size, error: !!props.error, disabled: params.disabled, sx: {
                '& label.MuiInputLabel-root': { paddingRight: 3 },
            } },
            React.createElement(OutlinedInput, Object.assign({}, params.InputProps, { inputProps: Object.assign({}, params.inputProps), id: params.id, placeholder: labelWithChipCount, fullWidth: params.fullWidth, size: params.size, error: !!props.error, disabled: params.disabled, sx: Object.assign({ backgroundColor: 'background.paper' }, {
                    '& input::placeholder': {
                        typography: 'body2',
                        color: themeColors.text.subtle,
                        opacity: 1,
                        textOverflow: 'ellipsis',
                    },
                }) }))));
    };
    const onChangeHandler = React.useMemo(() => (_event, selectedItems) => {
        props.onSelectionChange(selectedItems.map((selectedItem) => selectedItem.value));
    }, [props]);
    const values = React.useMemo(() => props.config.items.filter((item) => { var _a; return (_a = props.value) === null || _a === void 0 ? void 0 : _a.includes(item.value); }), [props]);
    return (React.createElement(Autocomplete, { multiple: true, disableListWrap: true, disableCloseOnSelect: true, openOnFocus: true, fullWidth: true, size: 'small', options: props.config.items, value: values, PopperComponent: StyledPopper, ListboxComponent: ListboxComponent, renderInput: renderInput, renderTags: formatTags, renderOption: renderOption, onChange: onChangeHandler }));
};
